import { Expose } from 'class-transformer';
import { format } from 'date-fns';
import { PccAuditInfo } from './audit-info.typings';

export type PccMedicationStatus = 'ACTIVE' | 'DISCONTINUED' | 'COMPLETED' | 'STRUCKOUT' | 'ONHOLD' | 'DRAFT' | 'UNVERIFIED' | 'UNCONFIRMED' | 'PENDING_REVIEW' | 'PENDING_MARK_TO_SIGN' | 'PENDING_SIGNATURE' | 'HISTORICAL';
export type PccMedicationStatusFilterOptions = 'active' | 'pending' | 'discontinued' | 'completed' | 'initial';
export type PccMedicationIncludeOptionalFields = 'drugclass' | 'importeddata' | 'physiciandetails';

export class PccMedicationParams {
	statuses?: PccMedicationStatusFilterOptions[];

	limitNonActiveOrders?: boolean;

	includeOptionalFields?: PccMedicationIncludeOptionalFields[];
}
/**
 * Represents a coding entry for administration route
 */
export class PccAdministrationRouteCoding {
	/**
	 * Specific code for the route of administration being conveyed
	 */
	code: string;

	/**
	 * Text description of the route of administration
	 */
	display: string;

	/**
	 * Type of coding system being used e.g. 'PointClickCare' or SNOMED. Can be a URL.
	 * If both SNOMED and PointClickCare codes are passed, only the PointClickCare codes will be saved.
	 * Supported systems are:
	 * PointClickCare: https://pointclickcare.com/system/administration-route
	 * SNOMED: http://snomed.info/sct
	 */
	system: string;
}

/**
 * Represents the route of administration for medication
 */
export class PccMedicationRoute {
	/**
	 * List of medication Diagnosis, can accept up to 3 entries
	 */
	coding: PccAdministrationRouteCoding[];
}

/**
 * Represents medication administration information
 */
export class PccMedicationAdministration {
	/**
	 * Route of administration of medication. Information includes coding system, specific route code and text description of route of administration.
	 */
	route: PccMedicationRoute;
}

/**
 * Represents physician address information
 */
export class PccPhysicianAddress {
	/**
	 * First line of address
	 */
	addressLine1: string;

	/**
	 * City name
	 */
	city: string;

	/**
	 * State or province
	 */
	state: string;

	/**
	 * Postal or ZIP code
	 */
	postalCode: string;

	/**
	 * Country name
	 */
	country: string;
}

/**
 * Represents physician details
 */
export class PccPhysicianDetail {
	/**
	 * First name of the physician
	 */
	firstName: string;

	/**
	 * Last name of the physician
	 */
	lastName: string;

	/**
	 * Address information
	 */
	address: PccPhysicianAddress;

	/**
	 * Cell number of the physician
	 */
	phoneNumber: number;

	/**
	 * Fax of the physician
	 */
	fax: number;

	/**
	 * NPI of the physician
	 */
	npi: number;

	/**
	 * State license number of the physician
	 */
	stateLicenseNumber: string;
}

/**
 * Represents imported data information
 */
export class PccImportedData {
	/**
	 * The start data of the order when it was imported
	 */
	startDate: string;

	/**
	 * The end date of the order when it was imported
	 */
	endDate: string;

	/**
	 * The status of the order when it was imported
	 */
	status: string;

	/**
	 * The directions provided for the order when it was imported
	 */
	directions: string;
}

/**
 * Represents dose unit of measure coding
 */
export class PccDoseUOMCoding {
	/**
	 * Code for the unit of measure
	 */
	code: string;

	/**
	 * Display name for the unit of measure
	 */
	display: string;

	/**
	 * System identifier for the coding
	 */
	system: string;
}

/**
 * Represents codeable dose unit of measure
 */
export class PccCodeableDoseUOM {
	/**
	 * Array of coding entries for the unit of measure
	 */
	coding: PccDoseUOMCoding[];
}

/**
 * Represents last administration details
 */
export class PccLastAdministration {
	/**
	 * Amount administered
	 */
	administeredAmount: string;

	/**
	 * User who administered the medication
	 */
	administeredBy: string;

	/**
	 * Date and time of administration
	 */
	administeredDateTime: string;

	/**
	 * Administration code
	 */
	code: string;

	/**
	 * Description of the administration code
	 */
	codeDescription: string;

	/**
	 * Whether administration was unable to be completed
	 */
	unableToAdminister: boolean;

	/**
	 * Represents the details of the last administration.
	 *
	 * SimpliScreen maps the last administration to the following string for embedding in the RAG:
	 * administeredAmount: {administeredAmount}, administeredBy: {administeredBy}, administeredDateTime: {administeredDateTime}, code: {code}, codeDescription: {codeDescription}, unableToAdminister: {unableToAdminister}
	 */
	details: string;

	constructor(partial?: Partial<PccLastAdministration>) {
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails() {
		if (this.unableToAdminister) return 'Unable to administer';
		let details = 'Administered';
		if (this.administeredBy) details += ` by ${this.administeredBy}`;
		if (this.administeredDateTime) details += ` on ${this.administeredDateTime}`;
		if (this.code) details += ` with code ${this.code}`;
		if (this.codeDescription) details += ` and code description ${this.codeDescription}`;
		return details;
	}
}

/**
 * Represents a medication schedule
 */
export class PccMedicationSchedule {
	/**
	 * Administration directions for each schedule, indications for use and other related text
	 */
	directions: string;

	/**
	 * The quantity of the medication
	 */
	dose: string;

	/**
	 * Unit of measure for medication quantity. Can be in metric units, imperial units or 'each' e.g. 'Capsule'.
	 */
	doseUOM: string;

	/**
	 * Dose unit of measure codeable concept
	 */
	codeableDoseUOM?: PccCodeableDoseUOM;

	/**
	 * The end date and time of schedule. Date and time are expressed in UTC (Coordinated Universal Time), with a special UTC designator ("Z").
	 */
	endDateTime?: string;

	/**
	 * Predefined list of possible medication administration frequencies from PointClickCare
	 */
	frequency: string;

	/**
	 * Indications for which medication was administered
	 */
	indicationsForUse: string;

	/**
	 * Last medication administration details for the schedule
	 */
	lastAdministration?: PccLastAdministration;

	/**
	 * Unique id which associate the schedule with the order
	 */
	orderScheduleId: number;

	/**
	 * Predefined list of possible medication administration schedule types from PointClickCare
	 */
	scheduleType: string;

	/**
	 * The start date and time of schedule. Date and time are expressed in UTC (Coordinated Universal Time), with a special UTC designator ("Z").
	 */
	startDateTime: string;

	/**
	 * Represents the details of the medication schedule.
	 *
	 * SimpliScreen maps the medication schedule to the following string for embedding in the RAG:
	 * scheduleType: {scheduleType}, frequency: {frequency}, dose: {dose}{doseUOM}, indicationsForUse: {indicationsForUse}, directions: {directions}, lastAdministration: {lastAdministration.administeredAmount}{lastAdministration.administeredBy}{lastAdministration.administeredDateTime}{lastAdministration.code}{lastAdministration.codeDescription}{lastAdministration.unableToAdminister}
	 */
	details: string;

	constructor(partial?: Partial<PccMedicationSchedule>) {
		Object.assign(this, partial);
		if (this.lastAdministration) this.lastAdministration = new PccLastAdministration(this.lastAdministration);
	}

	@Expose({ name: 'details' })
	getDetails(prefix = '') {
		const details = [];
		if (this.scheduleType) details.push(`Schedule Type: ${this.scheduleType}`);
		if (this.frequency) details.push(`Frequency: ${this.frequency}`);
		if (this.dose) details.push(`Dose: ${this.dose}${this.doseUOM}`);
		if (this.indicationsForUse) details.push(`Indications For Use: ${this.indicationsForUse}`);
		if (this.directions) details.push(`Directions: ${this.directions}`);
		if (this.lastAdministration) details.push(`Last Administration Details: ${this.lastAdministration.getDetails()}`);
		return `${prefix}${details.join(', ')}`;
	}
}

/**
 * Represents a medication record for a patient in PointClickCare
 *
 * SimpliScreen maps the medication to the following string for embedding in the RAG:
 * description: {description}, generic: {generic}, directions: {directions}, status: {status}, strength: {strength}{strengthUOM}, startDate: {startDate}, endDate: {endDate}, schedules: {schedules}
 * \t scheduleType: {scheduleType}, frequency: {frequency}, dose: {dose}{doseUOM}, indicationsForUse: {indicationsForUse}, directions: {directions}, lastAdministration: {lastAdministration.administeredAmount}{lastAdministration.administeredBy}{lastAdministration.administeredDateTime}{lastAdministration.code}{lastAdministration.codeDescription}{lastAdministration.unableToAdminister}
 *
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/medications?patientId={patientId}
 */
export class PccMedication extends PccAuditInfo {
	/**
	 * Medication Administration
	 */
	administration?: PccMedicationAdministration;

	/**
	 * PointClickCare's unique identifier for a patient within the organization.
	 *
	 * When a patient is admitted to a facility within an organization, they are assigned a clientId which is unique to their stay within that facility. If the same patient were to get admitted to another facility within the same organization in the future, they would be assigned a new clientId. The clientId however is unique at the organization level. That is, no two patients across different facilities within the organization will have the same clientId.
	 *
	 * This identifier is crucial for tracking patient information and care but must not be displayed in any user interface due to legal privacy requirements.
	 */
	clientId: number;

	/**
	 * Value of the narcotic level for the medication
	 */
	controlledSubstanceCode?: number;

	/**
	 * Dispensable Drug Id from Medispan drug database. May be blank if the medication is not from Medispan.
	 */
	ddId?: number;

	/**
	 * Description of the medication, including form and strength, if applicable
	 */
	description: string;

	/**
	 * Administration directions, including schedule, indications for use and other related text
	 */
	directions: string;

	/**
	 * The discontinue date and time of the medication. This field is only applicable when status='Discontinued' for the medication. Date and time are expressed in UTC (Coordinated Universal Time), with a special UTC designator ("Z").
	 */
	discontinueDate?: string;

	/**
	 * The last valid date and time of the medication. It may be empty if the medication has infinite duration. Date and time are expressed in UTC (Coordinated Universal Time), with a special UTC designator ("Z").
	 */
	endDate?: string;

	/**
	 * Generic drug name for this medication
	 */
	generic: string;

	/**
	 * It provides historical information about start date, end date, status and direction
	 */
	importedData?: PccImportedData;

	/**
	 * Indicates if is a narcotic
	 */
	narcotic?: boolean;

	/**
	 * Medication creation date
	 */
	orderDate: string;

	/**
	 * PointClickCare's unique identifier for the medication order
	 */
	orderId: number;

	/**
	 * Provides details of the ordering physician including firstname, lastname, fax, npi, address, state license number etc.
	 */
	physicianDetail?: PccPhysicianDetail;

	/**
	 * Product Name Id from Medispan drug database. May be blank if the medication is not from Medispan.
	 */
	pnId?: number;

	/**
	 * Name of the resident to whom the order belongs
	 */
	residentName: string;

	/**
	 * Another unique identifier for a resident that is permissible for display in external systems
	 */
	residentNumber: string;

	/**
	 * RXNorm Id from Unified Medical Language System® (UMLS®), https://www.nlm.nih.gov/research/umls/rxnorm/ May be blank if the medication is not from Medispan.
	 */
	rxNormId?: string;

	/**
	 * List of schedules for this medication
	 */
	schedules: PccMedicationSchedule[];

	/**
	 * The earliest administration date and time scheduled for this medication. Date and time are expressed in UTC (Coordinated Universal Time), with a special UTC designator ("Z").
	 */
	startDate: string;

	/**
	 * Status of the medication. After the medication is created and has passed all reviews, it becomes "ACTIVE" If the administration of the medication has been suspended, it becomes "ONHOLD" If the administration of the medication has finished in its course, by schedule, it becomes "COMPLETED" If the administration of the medication has finished by manual intervention, it becomes "DISCONTINUED" If the newly created medication needs to be confirmed by a doctor, it becomes "UNCONFIRMED" If the newly created medication needs to be reviewed by a doctor, it becomes "PENDING_REVIEW" Certain classes of newly created medications (e.g. narcotics) may require doctor signature before becoming active. Until then, they stay as "PENDING_SIGNATURE" Certain classes of newly created medications (e.g. narcotics) may require a two-step process to get doctor's signature before becoming active. Before the first phase is complete, they stay as "PENDING_MARK_TO_SIGN".
	 */
	status: PccMedicationStatus;

	/**
	 * Strength amount
	 */
	strength: string;

	/**
	 * Strength unit of measure
	 */
	strengthUOM: string;

	/**
	 * Represents the details of the medication.
	 *
	 * SimpliScreen maps the medication to the following string for embedding in the RAG:
	 * description: {description}, generic: {generic}, directions: {directions}, status: {status}, strength: {strength}{strengthUOM}, startDate: {startDate}, endDate: {endDate}, schedules: {schedules}
	 * \t scheduleType: {scheduleType}, frequency: {frequency}, dose: {dose}{doseUOM}, indicationsForUse: {indicationsForUse}, directions: {directions}, lastAdministration: {lastAdministration.administeredAmount}{lastAdministration.administeredBy}{lastAdministration.administeredDateTime}{lastAdministration.code}{lastAdministration.codeDescription}{lastAdministration.unableToAdminister}
	 */
	details: string;

	constructor(partial?: Partial<PccMedication>) {
		super();
		Object.assign(this, partial);
		this.schedules = this.schedules.map((schedule) => new PccMedicationSchedule(schedule));
		if ((partial as any).revDate) {
			this.revisionDate = (partial as any).revDate;
			this.revisionDateFormatted = format(this.revisionDate, 'PPP');
		}
	}

	@Expose({ name: 'details' })
	getDetails() {
		const details = [`Data Type: Medication`];
		if (this.orderId) details.push(`Order ID: ${this.orderId}`);
		if (this.description) details.push(`Description: ${this.description}`);
		if (this.generic) details.push(`Generic: ${this.generic}`);
		if (this.directions) details.push(`Directions: ${this.directions}`);
		if (this.status) details.push(`Status: ${this.status}`);
		if (this.strength) details.push(`Strength: ${this.strength}${this.strengthUOM}`);
		if (this.startDate) details.push(`Start Date: ${this.startDate}`);
		if (this.endDate) details.push(`End Date: ${this.endDate}`);
		return `${details.join(', ')}\n${this.schedules.map((schedule) => schedule.getDetails('\t')).join('\n')}`;
	}
}
